import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, Download, Plus, Upload, UploadCloud, X } from 'lucide-react'
import { importEmployees } from '../../lib/api'
import { refreshRoster } from '../../lib/roster'
// Reuse the proven, generic parsing primitives from the client intake pipeline.
import { parseFile, parseDelimited, realignTable } from '../client/intake/parse'
import type { ParsedTable } from '../client/intake/types'
import { downloadTeamTemplate } from '../../lib/download-template'
import { useDisciplines } from '../../hooks/useDisciplines'
import { FIELDS, autoMap, mapRows, type FieldKey } from './mapTeam'

const ACCEPT = '.csv,.tsv,.txt,.xlsx,.xlsm,.xlsb,.xls,.ods'
const MAX_IMPORT = 200
const PREVIEW = 8

const SAMPLE = `name\temail\ttitle\trole\tdisciplines\tstatus
Sarah Chen\tsarah@company.com\tMarketing Lead\tmember\tmarketing\troster only
Marcus Webb\tmarcus@company.com\tOps Manager\tadmin\tops; sales\tinvite
Dana Ruiz\tdana@company.com\tAccount Exec\tmember\tsales\troster only`

interface TeamImportProps {
  onClose: () => void
}

export function TeamImport({ onClose }: TeamImportProps) {
  const qc = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [table, setTable] = useState<ParsedTable | null>(null)
  const [colMap, setColMap] = useState<Record<number, FieldKey | null>>({})
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [parsing, setParsing] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [skipInvalid, setSkipInvalid] = useState(true)
  const [result, setResult] = useState<{ count: number; skipped: { email: string; reason: string }[] } | null>(null)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  const disciplines = useDisciplines()
  const mapped = useMemo(
    () => (table ? mapRows(table, colMap, disciplines) : []),
    [table, colMap, disciplines],
  )
  const valid = mapped.filter((r) => r.errors.length === 0)
  const invalid = mapped.filter((r) => r.errors.length > 0)
  const importable = skipInvalid ? valid : mapped
  const mappedKeys = new Set(Object.values(colMap).filter(Boolean))
  const missing = FIELDS.filter((f) => f.required && !mappedKeys.has(f.key))

  const mutation = useMutation({
    mutationFn: () => importEmployees(importable.map((r) => r.draft)),
    onSuccess: (res) => {
      refreshRoster(qc)
      if (res.skipped.length > 0) setResult({ count: res.count, skipped: res.skipped })
      else onClose()
    },
  })

  const adoptTable = (parsed: ParsedTable) => {
    // Skip banner/preamble rows above the real header (common in xlsx exports).
    const aligned = realignTable(parsed, ['name', 'email'])
    setTable(aligned)
    setColMap(autoMap(aligned.headers))
  }

  const handleFile = async (file: File) => {
    setError(null)
    setParsing(true)
    try {
      const parsed = await parseFile(file, { sheetHint: /team|role|member|staff|people/i })
      if (!parsed || parsed.rows.length === 0) setError('No data rows found — need a header row plus records.')
      else adoptTable(parsed)
    } catch (err) {
      setError(err instanceof Error ? `Could not read the file: ${err.message}` : 'Could not read the file.')
    } finally {
      setParsing(false)
    }
  }

  const parsePasted = () => {
    setError(null)
    const parsed = parseDelimited(text)
    if (!parsed || parsed.rows.length === 0) setError('Paste a header row and at least one member row.')
    else adoptTable(parsed)
  }

  const setColumn = (colIdx: number, key: FieldKey | null) => {
    setColMap((prev) => {
      const next = { ...prev, [colIdx]: key }
      // A destination field maps to at most one column — clear any prior owner.
      if (key) {
        for (const k of Object.keys(next)) {
          const idx = Number(k)
          if (idx !== colIdx && next[idx] === key) next[idx] = null
        }
      }
      return next
    })
  }

  const canImport =
    missing.length === 0 &&
    importable.length > 0 &&
    importable.length <= MAX_IMPORT &&
    (skipInvalid || invalid.length === 0)

  return (
    <div className="quick-add-backdrop" onClick={onClose}>
      <div className="intake-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Import team members">
        <div className="intake-header">
          <span className="intake-header-icon"><Upload size={18} /></span>
          <div className="intake-header-text">
            <h3>Import team members</h3>
            <p>Bulk-load your roster from a spreadsheet or pasted rows.</p>
          </div>
          <button onClick={onClose} className="btn-icon intake-close" aria-label="Close"><X size={18} /></button>
        </div>

        <div className="intake-content">
          {result ? (
            <div className="intake-import-review">
              <div className="intake-import-stats">
                <span className="intake-stat intake-stat--ok">
                  <CheckCircle2 size={14} /> Imported {result.count} member{result.count === 1 ? '' : 's'}
                </span>
                <span className="intake-stat intake-stat--bad">
                  <AlertTriangle size={14} /> {result.skipped.length} skipped
                </span>
              </div>
              <ul className="text-muted" style={{ fontSize: 12, margin: 0, paddingLeft: 18 }}>
                {result.skipped.map((s) => (
                  <li key={s.email}>{s.email} — {s.reason}</li>
                ))}
              </ul>
              <div className="intake-footer">
                <button type="button" className="btn btn-primary btn-sm" onClick={onClose}>Done</button>
              </div>
            </div>
          ) : !table ? (
            <>
              <button
                type="button"
                className={`intake-dropzone${dragging ? ' intake-dropzone--active' : ''}`}
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f) }}
              >
                <span className="intake-dropzone-icon"><UploadCloud size={26} /></span>
                <span className="intake-dropzone-title">{parsing ? 'Reading…' : 'Drop a spreadsheet or click to browse'}</span>
                <span className="intake-dropzone-sub">CSV, TSV, or Excel · up to {MAX_IMPORT} members</span>
                <input ref={inputRef} type="file" accept={ACCEPT} hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f) }} />
              </button>

              <p className="intake-eyebrow" style={{ marginTop: 'var(--space-2)' }}>or paste rows</p>
              <textarea
                className="intake-input intake-textarea intake-paste-area"
                rows={5}
                placeholder={'name, email, title, role, disciplines, status\nSarah Chen, sarah@company.com, Marketing Lead, member, marketing, roster only'}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              {error && <p className="intake-errors intake-errors--block">{error}</p>}
              <div className="intake-footer">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setText(SAMPLE)}>Use sample</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => downloadTeamTemplate(disciplines)}><Download size={14} /> Download template</button>
                <button type="button" className="btn btn-primary btn-sm" onClick={parsePasted} disabled={!text.trim()}>Parse rows</button>
              </div>
            </>
          ) : (
            <div className="intake-import-review">
              <div className="intake-import-summary">
                <div className="intake-import-source">
                  <span className="intake-import-source-name">{table.source}</span>
                  <span className="intake-import-source-meta">{table.rows.length} rows · {table.headers.length} columns</span>
                </div>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setTable(null); setError(null) }}>Start over</button>
              </div>

              {missing.length > 0 ? (
                <div className="intake-banner intake-banner--warn">
                  <AlertTriangle size={15} />
                  <span>Map a column to <strong>{missing.map((m) => m.label).join(', ')}</strong> to continue — it&apos;s required.</span>
                </div>
              ) : (
                <div className="intake-import-stats">
                  <span className="intake-stat intake-stat--ok"><CheckCircle2 size={14} /> {valid.length} ready</span>
                  {invalid.length > 0 && <span className="intake-stat intake-stat--bad"><AlertTriangle size={14} /> {invalid.length} with issues</span>}
                </div>
              )}

              <div className="intake-table-scroll">
                <table className="intake-table">
                  <thead>
                    <tr>
                      <th className="intake-table-rownum" />
                      {table.headers.map((h, i) => (
                        <th key={i}>
                          <div className="intake-col-header" title={h}>{h || '(blank)'}</div>
                          <select
                            className="intake-col-select"
                            value={colMap[i] ?? ''}
                            onChange={(e) => setColumn(i, (e.target.value || null) as FieldKey | null)}
                          >
                            <option value="">— Ignore —</option>
                            {FIELDS.map((f) => (
                              <option key={f.key} value={f.key}>{f.label}{f.required ? ' *' : ''}</option>
                            ))}
                          </select>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {mapped.slice(0, PREVIEW).map((row, ri) => (
                      <tr key={ri} className={row.errors.length ? 'intake-table-row--bad' : ''}>
                        <td className="intake-table-rownum mono" title={row.errors.join('\n')}>
                          {row.errors.length ? <AlertTriangle size={12} /> : row.sourceRow}
                        </td>
                        {table.headers.map((_, ci) => (
                          <td key={ci} className={colMap[ci] ? '' : 'intake-cell--ignored'}>{table.rows[ri]?.[ci] || ''}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {mapped.length > PREVIEW && <p className="intake-preview-more">Showing first {PREVIEW} of {mapped.length} rows.</p>}

              {importable.length > MAX_IMPORT && (
                <div className="intake-banner intake-banner--warn">
                  <AlertTriangle size={15} />
                  <span>{importable.length} rows exceeds the {MAX_IMPORT}-row limit. Split the file and import in batches.</span>
                </div>
              )}

              <div className="intake-footer">
                <label className="intake-check">
                  <input type="checkbox" checked={skipInvalid} onChange={(e) => setSkipInvalid(e.target.checked)} disabled={invalid.length === 0} />
                  <span>Skip {invalid.length} row{invalid.length === 1 ? '' : 's'} with issues</span>
                </label>
                <button type="button" className="btn btn-primary btn-sm" disabled={!canImport || mutation.isPending} onClick={() => mutation.mutate()}>
                  <Plus size={14} />{mutation.isPending ? 'Importing…' : `Import ${importable.length} member${importable.length === 1 ? '' : 's'}`}
                </button>
              </div>
              {mutation.isError && <p className="intake-errors intake-errors--block">{(mutation.error as Error).message}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
